import { Route } from '@ant-design/pro-layout/lib/typings';
import { BasicLayout } from '@tencent/tds-ui/src/layout';
import { TdsFooter } from '@tencent/tds-ui/src/tds-footer';
import { useSize } from 'ahooks';
import { Spin } from 'antd';
import classnames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BuglyCopilotIcon from '@/assets/images/header/bugly-copilot-icon.svg?react';
import BuglyIcon from '@/assets/images/public/bugly2.0.react.svg?react';
import { isInternal } from '@/infrastructure/auth';
import { pushHistory } from '@/infrastructure/history';
import { logger } from '@/infrastructure/logger';
import { Banner } from '@/presentation/components/banner';
import { OperationNotice } from '@/presentation/components/operation-notice';
import { useAuthContext } from '@/presentation/hooks';
import { Content } from '@/presentation/layouts/content';
import { TopMenu } from '@/presentation/layouts/header';
import { AppList } from '@/presentation/layouts/header/app-list';
import { Tools } from '@/presentation/layouts/tools';
import { getRoutes } from '@/presentation/routes/routes';
import { stores, useStores } from '@/presentation/store';
import { CopilotAgentWrapper } from './header/copilot/agent';
import style from './index.module.scss';
export const AppLayout = observer(() => {
  const { loaded, productId, hasProduct } = stores.ProductStore;
  const {
    setCollapsed,
    layoutLoading,
    loadingMessage,
    copilotChatVisible,
    copilotChatWidth,
    setCopilotVisible,
    collapsed,
  } = useStores().LayoutStore;
  const { authInfo } = useAuthContext();
  const routes = getRoutes();
  const leftMenuRoutes = { routes };
  const history = useHistory();
  const location = useLocation();

  // 悬浮球拖拽相关状态
  const [ballPosition, setBallPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const ballRef = useRef<HTMLDivElement>(null);

  const handlePath = (path: string) => {
    pushHistory(history, path);
  };

  const onOpenFromFloatingBall = () => {
    // 只有在没有拖拽时才打开面板
    if (!isDragging) {
      setCopilotVisible(true);
    }
  };

  // 处理鼠标按下
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    dragStartPos.current = { x: e.clientX, y: e.clientY };

    // 拖拽时禁用 transition
    if (ballRef.current) {
      ballRef.current.style.transition = 'none';
    }

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = Math.abs(moveEvent.clientX - dragStartPos.current.x);
      const deltaY = Math.abs(moveEvent.clientY - dragStartPos.current.y);

      // 移动超过5px才认为是拖拽
      if (deltaX > 5 || deltaY > 5) {
        setIsDragging(true);
      }

      if (ballRef.current) {
        const rect = ballRef.current.getBoundingClientRect();
        const newX = moveEvent.clientX - rect.width / 2;
        const newY = moveEvent.clientY - rect.height / 2;

        // 限制在视口范围内
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;

        const clampedX = Math.max(0, Math.min(newX, maxX));
        const clampedY = Math.max(0, Math.min(newY, maxY));

        // 直接操作 DOM，避免 React 状态更新延迟
        ballRef.current.style.left = `${clampedX}px`;
        ballRef.current.style.top = `${clampedY}px`;
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      // 恢复 transition（用于 hover 效果）
      if (ballRef.current) {
        ballRef.current.style.transition = '';
      }

      // 同步最终位置到 state
      if (ballRef.current) {
        const left = parseFloat(ballRef.current.style.left) || 0;
        const top = parseFloat(ballRef.current.style.top) || 0;
        setBallPosition({ x: left, y: top });
      }

      // 延迟重置拖拽状态，避免触发点击
      setTimeout(() => setIsDragging(false), 100);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const isConfig = location.pathname.includes('/config-manage');
  const windowSize = useSize(window.document.body);
  const liteModeRoutes = ['/workbench/apps', '/user/profile', '/user/token', '/config-manage', '/git/oauth', '/', '/silence'];
  const inlineCopilotRoutes: string[] = [];

  useEffect(() => {
    logger.info(
      'AppLayout|render',
      productId,
      location.pathname,
      loaded,
      authInfo,
    );
    // 无产品选中，跳首页 登录了情况才跳
    if (loaded && !productId && !liteModeRoutes.includes(location.pathname)) {
      // pushHistory(history, '/index/main');
      window.location.href = '/';
    }
  }, [loaded, productId]);

  useEffect(() => {
    if (isInternal() && authInfo?.isLoggedIn) {
      stores.ProductStore.initForInternal();
    }
  }, []);

  useEffect(() => {
    const layoutHeader = document.querySelector('#layoutWithChatContainer .ant-pro-fixed-header');

    if (copilotChatVisible) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }

    if (!copilotChatVisible && layoutHeader instanceof HTMLElement) {
      layoutHeader.style.width = '100%';
      return;
    }

    if (copilotChatVisible && layoutHeader instanceof HTMLElement) {
      const windowWidth = window.innerWidth;
      layoutHeader.style.width = `${windowWidth - copilotChatWidth}px`;
    }
  }, [copilotChatWidth, copilotChatVisible, windowSize?.width]);

  // 没登录不render
  if (!authInfo?.isLoggedIn) {
    return null;
  }

  if (location.pathname === '/') {
    return <Content />;
  }

  return (
    <Spin
      spinning={layoutLoading}
      tip={loadingMessage}
      className={style.loading}
    >
      <div className={style.layoutWithChat} id='layoutWithChatContainer'>
        <div className={style.withoutChat} id='layoutWithoutChatContainer'>
          <BasicLayout
            menuProps={{
              className: style.menu,
            }}
            liteMode={!hasProduct}
            showBreadcrumb={hasProduct}
            mainMenuLink="/overview"
            className={classnames({
              [style.app]: true,
              [style.config]: isConfig,
            })}
            leftMenuRoutes={leftMenuRoutes as Route}
            appSelector={<AppList />}
            topMenu={<TopMenu />}
            liteModeRoutes={liteModeRoutes}
            logo={
              <a href='/'>
                <BuglyIcon />
              </a>
            }
            setCollapsed={setCollapsed}
            collapsed={collapsed}
            handlePath={handlePath}
            title={'Bugly'}
            renderBanner={(showBanner, setShowBanner) => (
              <Banner
                showBanner={showBanner}
                setShowBanner={setShowBanner}
                liteModeRoutes={liteModeRoutes}
              />
            )}
          >
            <div className={classnames(style.containerLayout, 'content-layout')}>
              <Content />
              <TdsFooter isWoa={!isInternal()} />
              <Tools />
            </div>
          </BasicLayout>
        </div>
        <div className={style.withChat}>
          {copilotChatVisible && <CopilotAgentWrapper />}
        </div>

        {/* 悬浮球 - 当 copilot 面板关闭时显示 */}
        {(!copilotChatVisible
          && !liteModeRoutes.includes(location.pathname)
          && !inlineCopilotRoutes.includes(location.pathname)
        ) && (
          <div
            ref={ballRef}
            className={style.floatingBall}
            onClick={onOpenFromFloatingBall}
            onMouseDown={handleMouseDown}
            style={{
              left: ballPosition.x || undefined,
              top: ballPosition.y || undefined,
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          >
            <BuglyCopilotIcon />
          </div>
        )}

        {/* 运营公告组件 */}
        <OperationNotice liteModeRoutes={liteModeRoutes} />
      </div>
    </Spin>
  );
});
